import React, { useEffect, useRef } from 'react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { StepConfig, EstimatorSelections, Feature } from './types';
import OptionCard from './OptionCard';

interface EstimatorStepProps {
  step: StepConfig;
  stepIndex: number;
  totalSteps: number;
  selections: EstimatorSelections;
  onSelect: (field: keyof EstimatorSelections, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  isFirst: boolean;
  animationClass: string;
}

const EstimatorStep: React.FC<EstimatorStepProps> = ({
  step,
  stepIndex,
  totalSteps,
  selections,
  onSelect,
  onNext,
  onBack,
  isFirst,
  animationClass,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const progress = Math.round(((stepIndex) / totalSteps) * 100);

  // Move focus to heading when step changes for screen reader orientation
  useEffect(() => {
    const t = setTimeout(() => headingRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [step.id]);

  const currentValue = selections[step.field];
  const isMulti = step.multiSelect;

  const selectedSingle = !isMulti ? (currentValue as string) : '';
  const selectedMulti = isMulti ? (currentValue as Feature[]) : [];

  const isSelected = (value: string): boolean =>
    isMulti
      ? selectedMulti.includes(value as Feature)
      : selectedSingle === value;

  const hasSelection = isMulti
    ? selectedMulti.length > 0
    : selectedSingle !== '';

  return (
    <div
      className={cn('w-full', animationClass)}
      role="group"
      aria-labelledby={`step-${step.id}-heading`}
    >
      {/* ─── Progress ──────────────────────────────────────────── */}
      <div className="mb-8 space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-[#B8883D]">
            Step {stepIndex + 1} of {totalSteps}
          </span>
          <span className="font-sans text-xs text-[#7A7367]" aria-hidden="true">
            {progress}% complete
          </span>
        </div>
        <Progress
          value={progress}
          className="h-1.5 bg-[#E8E2D9]"
          aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
        />
      </div>

      {/* ─── Heading ───────────────────────────────────────────── */}
      <div className="mb-8">
        <h2
          id={`step-${step.id}-heading`}
          ref={headingRef}
          tabIndex={-1}
          className="font-serif text-2xl md:text-3xl font-bold text-[#1A412D] mb-3 outline-none"
        >
          {step.title}
        </h2>
        <p className="font-sans text-sm md:text-base text-[#7A7367] leading-relaxed max-w-lg">
          {step.subtitle}
        </p>
        {isMulti && (
          <p className="font-sans text-xs text-[#B8883D] mt-2 font-semibold">
            Select all that apply
          </p>
        )}
      </div>

      {/* ─── Options Grid ──────────────────────────────────────── */}
      <div
        className={cn(
          'grid gap-3',
          step.options.length <= 3
            ? 'grid-cols-1'
            : 'grid-cols-1 sm:grid-cols-2',
        )}
        role={isMulti ? 'group' : 'radiogroup'}
        aria-label={step.title}
      >
        {step.options.map((option) => (
          <OptionCard
            key={option.value}
            option={option}
            selected={isSelected(option.value)}
            multiSelect={isMulti}
            onSelect={(value) => onSelect(step.field, value)}
          />
        ))}
      </div>

      {/* ─── Navigation ───────────────────────────────────────── */}
      <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#E8E2D9]">
        {!isFirst ? (
          <Button
            variant="ghost"
            onClick={onBack}
            className="font-sans font-medium text-[#7A7367] hover:text-[#1A412D] hover:bg-[#F8F5F1] gap-2 px-4 py-2 rounded-xl transition-colors"
            aria-label="Go back to previous step"
          >
            <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4" aria-hidden="true">
              <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </Button>
        ) : (
          <div aria-hidden="true" />
        )}

        <Button
          onClick={onNext}
          disabled={!hasSelection}
          className={cn(
            'font-sans font-semibold px-7 py-2.5 rounded-xl transition-all duration-200',
            'bg-[#1A412D] text-white hover:bg-[#142e1f] shadow-md',
            'disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none',
            'focus-visible:ring-2 focus-visible:ring-[#1A412D] focus-visible:ring-offset-2',
          )}
          aria-label={
            !hasSelection
              ? 'Select an option to continue'
              : stepIndex === 4
              ? 'Continue to last step'
              : 'Continue to next step'
          }
        >
          {isMulti && selectedMulti.length === 0
            ? 'Select features to continue'
            : 'Continue'}
          <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4 ml-2 inline-block" aria-hidden="true">
            <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Button>
      </div>
    </div>
  );
};

export default EstimatorStep;
